interface AnimatedWebpConfig {
  threshold: number;
  rootMargin: string;
  zoneStart: number; // poměr odshora (0–1), výchozí 0.25
  zoneEnd: number; // poměr odshora (0–1), výchozí 0.75
}

interface AnimatedWebpInstance {
  destroy: () => void;
  updateConfig: (newConfig: Partial<AnimatedWebpConfig>) => void;
  updateVisibleElement: () => void;
}

const createAnimatedWebpImage = (
  config: Partial<AnimatedWebpConfig> = {}
): AnimatedWebpInstance => {
  let isMobile: boolean;
  let isLandscape: boolean;
  let currentlyAnimatedElement: HTMLElement | null = null;
  let loopId: number | null = null;

  const defaultConfig: AnimatedWebpConfig = {
    threshold: 1.0,
    rootMargin: '-80px 0px 0px 0px',
    zoneStart: 0.25,
    zoneEnd: 0.75,
    ...config,
  };

  const SELECTORS = {
    animatedWebp: '.global-image__animated-webp',
    staticImage: '.global-image__img--has-animated-webp',
    container: '.global-image__container--has-animated-webp',
  };

  const detectMobile = (): boolean => window.innerWidth <= 679;
  const detectLandscape = (): boolean => window.innerWidth > window.innerHeight;

  const showAnimatedWebp = (
    animatedWebp: HTMLImageElement,
    staticImage: HTMLImageElement,
    container: HTMLElement
  ): void => {
    if (currentlyAnimatedElement && currentlyAnimatedElement !== container) {
      const prevAnimatedWebp = currentlyAnimatedElement.querySelector(
        SELECTORS.animatedWebp
      ) as HTMLImageElement;
      const prevStaticImage = currentlyAnimatedElement.querySelector(
        SELECTORS.staticImage
      ) as HTMLImageElement;

      if (prevAnimatedWebp && prevStaticImage) {
        prevAnimatedWebp.style.opacity = '0';
        prevStaticImage.style.opacity = '1';
      }
    }

    animatedWebp.style.opacity = '1';
    staticImage.style.opacity = '0';
    currentlyAnimatedElement = container;
  };

  const hideAnimatedWebp = (
    animatedWebp: HTMLImageElement,
    staticImage: HTMLImageElement
  ): void => {
    animatedWebp.style.opacity = '0';
    staticImage.style.opacity = '1';
  };

  const updateVisibleElement = (): void => {
    if (!isMobile || isLandscape) return;

    const containers = Array.from(document.querySelectorAll(SELECTORS.container)) as HTMLElement[];

    const viewportHeight = window.innerHeight;
    const menuOffset = 68; // horní menu – přizpůsob podle reality
    const upperBound = viewportHeight * defaultConfig.zoneStart + menuOffset;
    const lowerBound = viewportHeight * defaultConfig.zoneEnd;
    const viewportMiddle = viewportHeight / 2;

    let closestContainer: HTMLElement | null = null;
    let closestDistance = Infinity;

    containers.forEach((container) => {
      const rect = container.getBoundingClientRect();
      const containerMiddle = rect.top + rect.height / 2;

      // prvek je vidět a jeho střed je mezi hranicemi
      if (rect.bottom > 0 && rect.top < viewportHeight) {
        if (containerMiddle >= upperBound && containerMiddle <= lowerBound) {
          const distance = Math.abs(containerMiddle - viewportMiddle);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestContainer = container;
          }
        }
      }
    });

    containers.forEach((container) => {
      const animatedWebp = container.querySelector(SELECTORS.animatedWebp) as HTMLImageElement;
      const staticImage = container.querySelector(SELECTORS.staticImage) as HTMLImageElement;
      if (!animatedWebp || !staticImage) return;

      if (container === closestContainer) {
        showAnimatedWebp(animatedWebp, staticImage, container);
      } else {
        hideAnimatedWebp(animatedWebp, staticImage);
      }
    });

    currentlyAnimatedElement = closestContainer;
  };

  const initMobileLoop = (): void => {
    const loop = () => {
      updateVisibleElement();
      loopId = requestAnimationFrame(loop);
    };
    loopId = requestAnimationFrame(loop);
  };

  const init = (): void => {
    isMobile = detectMobile();
    isLandscape = detectLandscape();

    if (!isMobile) return;

    initMobileLoop(); // smyčka pro mobil
    updateVisibleElement(); // okamžitý první check
  };

  const destroy = (): void => {
    if (loopId !== null) {
      cancelAnimationFrame(loopId);
      loopId = null;
    }

    const containers = Array.from(document.querySelectorAll(SELECTORS.container)) as HTMLElement[];

    containers.forEach((container) => {
      const animatedWebp = container.querySelector(SELECTORS.animatedWebp) as HTMLImageElement;
      const staticImg = container.querySelector(SELECTORS.staticImage) as HTMLImageElement;

      if (animatedWebp && staticImg) {
        animatedWebp.style.opacity = '0';
        staticImg.style.opacity = '1';
      }
    });

    currentlyAnimatedElement = null;
  };

  const updateConfig = (newConfig: Partial<AnimatedWebpConfig>): void => {
    Object.assign(defaultConfig, newConfig);
    updateVisibleElement();
  };

  init();

  return {
    destroy,
    updateConfig,
    updateVisibleElement,
  };
};

// inicializace při načtení DOM
export const initAnimatedWebpImages = (): void => {
  createAnimatedWebpImage();
};

export default createAnimatedWebpImage;
